import { createPublicClient, http } from 'viem';
import {
  mainnet,
  arbitrum,
  arbitrumSepolia,
  sepolia,
  optimism,
  optimismSepolia,
  base,
  baseSepolia,
  type Chain
} from 'viem/chains';
import { blessnetMainnet, blessnetSepolia } from '@/config/bless-chain';
import { isMainnet } from '@/constants/flags.constant';

/**
 * Maps chain IDs to their corresponding Chain objects
 */
const CHAIN_MAP: Record<number, Chain> = {
  // Mainnet chains
  1: mainnet,
  42161: arbitrum,
  10: optimism,
  8453: base,
  45513: blessnetMainnet,

  // Testnet chains
  11155111: sepolia,
  421614: arbitrumSepolia,
  11155420: optimismSepolia,
  84532: baseSepolia,
  11145513: blessnetSepolia,
};

/**
 * Creates a public client for a specific chain ID using reliable public RPC endpoints
 * This allows making contract calls on any supported chain regardless of the user's connected wallet chain
 * Uses public RPCs to avoid API rate limits
 *
 * @param chainId - The chain ID to create a client for
 * @returns PublicClient for the specified chain, or null if chain is not supported
 */
export function getChainClient(chainId: number) {
  const chain = CHAIN_MAP[chainId];

  if (!chain) {
    console.warn(`Unsupported chain ID: ${chainId}`);
    return null;
  }

  // Use reliable public RPC endpoints
  const publicRpcUrls: Record<number, string> = isMainnet ? {
    1: "https://ethereum-rpc.publicnode.com",
    42161: "https://arbitrum-one-rpc.publicnode.com",
    10: "https://optimism-rpc.publicnode.com",
    8453: "https://base-rpc.publicnode.com",
    45513: blessnetMainnet.rpcUrls.default.http[0], // Use BlessNet's own RPC
  } : {
    11155111: "https://ethereum-sepolia-rpc.publicnode.com",
    421614: "https://arbitrum-sepolia-rpc.publicnode.com",
    11155420: "https://optimism-sepolia-rpc.publicnode.com",
    84532: "https://base-sepolia-rpc.publicnode.com",
    11145513: blessnetSepolia.rpcUrls.default.http[0], // Use BlessNet's own RPC
  };

  const rpcUrl = publicRpcUrls[chainId];
  if (rpcUrl) {
    return createPublicClient({
      chain,
      transport: http(rpcUrl),
    });
  }

  // Final fallback to chain's default RPC
  return createPublicClient({
    chain,
    transport: http(),
  });
}

/**
 * Gets all supported chain IDs based on the current environment (mainnet/testnet)
 */
export function getSupportedChainIds(): number[] {
  if (isMainnet) {
    return [1, 42161, 10, 8453, 45513]; // mainnet chains
  } else {
    return [11155111, 421614, 11155420, 84532, 11145513]; // testnet chains
  }
}
